import { ReactElement } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Pill, getButtonStyles } from '@sc/shared-ui';
import classNames from 'classnames';
import env from '../environment';
import {
  CLASSIFY_EFFORT_CREDIT_BREAKDOWN,
  formatPriceGbp,
  formatPricePerCredit,
  GBP_PER_CREDIT,
  ORG_CONCURRENT_CATEGORY_DRAINS,
  PER_PROJECT_CONCURRENT_CATEGORY_DRAINS,
  USAGE_CREDIT_STEPS,
  creditsForSpendGbp,
} from '../assets/data/pricing';

const PlanCard = ({
  name,
  tagline,
  headline,
  body,
  action,
  secondaryAction,
  highlighted,
}: {
  name: string;
  tagline: string;
  headline: string;
  body: string;
  action: ReactElement;
  secondaryAction?: ReactElement;
  highlighted?: boolean;
}): ReactElement => (
  <div
    className={classNames(
      'bg-background-tertiary flex flex-col rounded-xs p-8 h-full',
      highlighted ? 'border border-primary-400' : 'border border-grey-t-800'
    )}
  >
    <div className="flex items-start justify-between gap-2">
      <h3 className="font-grotesque text-2xl font-semibold text-white">
        {name}
      </h3>
      {highlighted && (
        <Pill
          size="small"
          colour="#55C78E"
          backgroundColour="#151F1E"
          outline
          text="Recommended"
        />
      )}
    </div>
    <p className="text-sm text-grey-200 mt-1">{tagline}</p>
    <p className="font-grotesque text-4xl lg:text-[2.75rem] leading-[1.15] font-medium text-white mt-8">
      {headline}
    </p>
    <p className="text-base leading-6 text-grey-200 mt-4 grow">{body}</p>
    <div className="flex items-center gap-5 mt-8">
      {action}
      {secondaryAction}
    </div>
  </div>
);

const primaryCta = (label: string, href: string): ReactElement => (
  <a
    href={href}
    target={href.startsWith('/') || href.startsWith('#') ? undefined : '_blank'}
    rel={
      href.startsWith('/') || href.startsWith('#')
        ? undefined
        : 'noreferrer noopener'
    }
    {...getButtonStyles({
      type: 'primary',
      size: 'large',
      additionalClassnames: 'gap-x-2',
    })}
  >
    {label}
    <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
  </a>
);

interface Props {
  calculatorHref?: string;
}

export function PricingPlans({
  calculatorHref = '#pricing-calculator',
}: Props): ReactElement {
  const firstCreditStep = USAGE_CREDIT_STEPS[0];
  if (!firstCreditStep) {
    throw new Error('USAGE_CREDIT_STEPS must not be empty');
  }
  const creditFrom = formatPriceGbp(firstCreditStep.priceGbp);
  const exampleCredits = creditsForSpendGbp(firstCreditStep.priceGbp);

  return (
    <div
      className="max-w-315 mx-auto grid gap-6 lg:grid-cols-1 lg:max-w-xl items-stretch"
      role="region"
      aria-label="Pricing plans"
    >
      <PlanCard
        highlighted
        name="Credits"
        tagline="Simple prepaid usage"
        headline={`${formatPricePerCredit(GBP_PER_CREDIT)}/credit`}
        body={`Buy credit packs or a monthly credit subscription from ${creditFrom} (${exampleCredits.toLocaleString('en-GB')} credits). Extraction effort: ${CLASSIFY_EFFORT_CREDIT_BREAKDOWN} credits per call. Crawls cost 1 credit per Firecrawl credit on the platform key. A project Firecrawl key is billed on your Firecrawl account and is not a Starkcrawl credit. Reclassify costs ~1 credit per call on the platform key (approximate). A Requesty key on the project skips that credit and is billed on your Requesty account using Jev model. Up to ${ORG_CONCURRENT_CATEGORY_DRAINS} concurrent category drains per org (${PER_PROJECT_CONCURRENT_CATEGORY_DRAINS} per project).`}
        action={primaryCta('Start for free', env.appUrl + '/register')}
        secondaryAction={
          <a
            href={calculatorHref}
            className="text-base font-semibold text-white underline underline-offset-4 hover:text-primary-400 transition-colors"
          >
            Estimate usage
          </a>
        }
      />
    </div>
  );
}
